import { Box, Text, Timer } from '@betty/moonlight';
import { useTranslation } from '@betty/i18n';
import React, { memo, useCallback, useRef } from 'react';
import { ShopSaleBannerOverlayProps } from './ShopSaleBannerOverlay.types';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  fetchShopItems,
  fetchShopSales,
  selectSalesSystemTimeDifference,
} from '../../state';
import { SaleStatus } from '../../state/sales/sales.types';
import { useShopFilters, useShopSaleTimer } from '../../hooks';
import { getSaleStatus } from '../../utils/shop.utils';

function ShopSaleBannerOverlay({ sale }: ShopSaleBannerOverlayProps) {
  const systemTimeDifference = useSelector(selectSalesSystemTimeDifference);
  const saleStatus = useRef(getSaleStatus(sale));
  const dispatch = useDispatch();
  const { locKey, timestamp, updateTimer } = useShopSaleTimer(sale);
  const { updateFilters, clearExpiredSaleFilter } = useShopFilters();
  const [t] = useTranslation('shop');

  const onTimerEnd = useCallback(() => {
    const currentSaleStatus = saleStatus.current;
    const nextSaleStatus = getSaleStatus(sale);
    // If the difference between system and client time is negative
    // it is posible to hit the endpoints earlier and fetch wrong data
    const delay = Math.abs(Math.min(0, systemTimeDifference));

    // Update timer text
    updateTimer();

    if (
      currentSaleStatus === SaleStatus.Upcoming &&
      nextSaleStatus === SaleStatus.Ongoing
    ) {
      // Update shop filters when the new sale starts
      updateFilters();
      setTimeout(() => dispatch(fetchShopItems()), delay);
    }

    if (
      currentSaleStatus === SaleStatus.Ongoing &&
      nextSaleStatus === SaleStatus.Ended
    ) {
      clearExpiredSaleFilter(sale.id);
      setTimeout(() => dispatch(fetchShopItems()), delay);
      setTimeout(() => dispatch(fetchShopSales()), delay);
    }

    saleStatus.current = nextSaleStatus;
  }, [
    dispatch,
    sale,
    systemTimeDifference,
    updateFilters,
    updateTimer,
    clearExpiredSaleFilter,
  ]);

  return (
    <Box absoluteFill centerContent gap="space-100">
      <Box
        variant="gradient"
        gradient="horizontalOverlayBackground"
        paddingHorizontal="space-1000"
        paddingVertical="space-100"
        justifyContent="center"
      >
        <Text
          textAlign="center"
          variant="callout"
          color="darkSurfaceAccentSolid"
        >
          {t('shopSaleLabel', {
            sale: sale.name,
            interpolation: { escapeValue: false },
          })}
        </Text>
        {timestamp && locKey ? (
          <Box flexDirection="row" gap="space-200" justifyContent="center">
            <Text
              variant="callout"
              color="darkSurfaceAccentSolid"
              locNS="shop"
              locKey={locKey}
            />
            <Timer
              color="darkSurfaceAccentSolid"
              variant="callout"
              time={timestamp}
              onCountDownComplete={onTimerEnd}
              unitSize="full"
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export default memo(ShopSaleBannerOverlay);
